import { z } from 'zod';

import { isLong, round2, signOf, type TradeType } from './primitives.ts';

const BROKER_ORDER_ID_MAX_LENGTH = 64;

const BROKER_REASON_MAX_LENGTH = 500;

// Kite rejects GTT triggers within 0.25% of LTP. Checked locally because Kite's
// refusal is indistinguishable from "not your trigger".
const GTT_LTP_CLEARANCE = 0.25 / 100;

export const TAG_MAX_LENGTH = 20;

export function tagFor(positionId: number): string {
  return `bb${positionId}`;
}

/** Distinct from the entry tag: the reconciler reads the last order under the entry tag as its fill. */
export function exitTagFor(positionId: number): string {
  return `bx${positionId}`;
}

/**
 * Kite postbacks carry no position; the tag is the only link. Members' own
 * orders arrive on the same webhook, hence null.
 */
export function readTag(tag: string): { leg: 'entry' | 'exit'; positionId: number } | null {
  const parsed = /^b([bx])([1-9]\d*)$/.exec(tag.trim());
  if (!parsed?.[1] || !parsed[2]) return null;
  const positionId = Number(parsed[2]);
  if (!Number.isSafeInteger(positionId)) return null;
  return { leg: parsed[1] === 'b' ? 'entry' : 'exit', positionId };
}

export const tagSchema = z
  .string()
  .min(1)
  .max(TAG_MAX_LENGTH)
  .regex(/^[A-Za-z0-9]+$/, 'a broker tag is alphanumeric only');

export const orderIdSchema = z.string().trim().min(1).max(BROKER_ORDER_ID_MAX_LENGTH);

export const reasonSchema = z.string().trim().min(1).max(BROKER_REASON_MAX_LENGTH);

const PRODUCTS = ['CNC', 'NRML'] as const;
export type Product = (typeof PRODUCTS)[number];

const CASH_EXCHANGES: ReadonlySet<string> = new Set(['NSE', 'BSE']);

/** Never MIS: positions must not be auto-squared-off. */
export function productFor(exchange: string): Product {
  return CASH_EXCHANGES.has(exchange.toUpperCase()) ? 'CNC' : 'NRML';
}

export const ORDER_SIDES = ['BUY', 'SELL'] as const;
export type OrderSide = (typeof ORDER_SIDES)[number];

export function isBuy(side: OrderSide): boolean {
  return side === 'BUY';
}

export function exitSide(tradeType: TradeType): OrderSide {
  return isLong(tradeType) ? 'SELL' : 'BUY';
}

export function entrySide(tradeType: TradeType): OrderSide {
  return exitSide(tradeType) === 'SELL' ? 'BUY' : 'SELL';
}

/**
 * Kite market protection: `-1` means exchange default, as Kite's own terminal
 * writes for market GTTs. Entry orders and GTT legs must agree.
 */
export const EXCHANGE_DEFAULT_MARKET_PROTECTION = -1;

// MCX quantities are in lots. Lot size comes from MCX_COMMODITIES, never Kite's dump (see config.ts).
const LOT_DENOMINATED_EXCHANGES = new Set(['MCX']);

export interface Sized {
  exchange: string;
  lotSize: number;
}

export function toOrderQuantity(sized: Sized, units: number): number {
  return LOT_DENOMINATED_EXCHANGES.has(sized.exchange.toUpperCase())
    ? units / sized.lotSize
    : units;
}

export function toUnits(sized: Sized, orderQuantity: number): number {
  return LOT_DENOMINATED_EXCHANGES.has(sized.exchange.toUpperCase())
    ? orderQuantity * sized.lotSize
    : orderQuantity;
}

export function levelStands(tradeType: TradeType, level: number, lastPrice: number): boolean {
  return signOf(tradeType) * (lastPrice - level) > 0;
}

export interface TooCloseLeg {
  stop: number;
  away: number;
  needed: number;
}

/** The nearest last price a stop at `level` clears the band from. */
export function bandClearedFrom(tradeType: TradeType, level: number): number {
  return level / (1 - signOf(tradeType) * GTT_LTP_CLEARANCE);
}

/**
 * Shared by the custodian and the ticket so both refuse the same levels. Null
 * when clear, and also when already breached.
 */
export function tooCloseToMarket(
  tradeType: TradeType,
  level: number,
  lastPrice: number,
): TooCloseLeg | null {
  if (!levelStands(tradeType, level, lastPrice)) return null;
  const needed = lastPrice * GTT_LTP_CLEARANCE;
  const away = signOf(tradeType) * (lastPrice - level);
  if (away >= needed) return null;
  // Compare at full precision (Kite's boundary); report rounded.
  return { stop: level, away: round2(away), needed: round2(needed) };
}

/** Kite's rows spell the exchange in either case. */
/** The day's circuit limits: the exchange refuses an order priced outside them. */
export interface PriceBand {
  lower: number;
  upper: number;
}

export function bandPassed(
  price: number,
  band: PriceBand | null | undefined,
): { side: 'above' | 'below'; edge: number } | null {
  if (!band) return null;
  if (price > band.upper) return { side: 'above', edge: band.upper };
  if (price < band.lower) return { side: 'below', edge: band.lower };
  return null;
}

/** A marketable limit reaching past the band is the same order at its edge. */
export const withinBand = (price: number, band: PriceBand | null, tradeType: TradeType): number =>
  band === null
    ? price
    : isLong(tradeType)
      ? Math.min(band.upper, price)
      : Math.max(band.lower, price);

export function sameLeg(
  a: { tradingsymbol: string; exchange: string },
  b: { tradingsymbol: string; exchange: string },
): boolean {
  return (
    a.tradingsymbol === b.tradingsymbol && a.exchange.toUpperCase() === b.exchange.toUpperCase()
  );
}
